import { Link } from 'react-router-dom'
import { CompletedReviewsSort, CompletedReviewsSortField } from '../../../services/MetricsDashboardService'
import { CompletedReview, TalentRankKey, formatDateLabel } from '../../../types/metrics'
import { createPathToEmployeeResults } from '../../../utils/employeePathUtils'
import useReviewsTableSort from '../../../hooks/useReviewsTableSort'
import DocumentNavigationSVG from '../../atoms/Icons/DocumentNavigation.svg'
import EmployeeCell from './EmployeeCell'
import ReviewsTableShell from './ReviewsTableShell'
import { SortableHeader } from './sortableHeader'
import { BaseReviewsTableProps } from './reviewsTableTypes'

type Props = BaseReviewsTableProps<CompletedReview, CompletedReviewsSort>

// Mirrors the backend default when the user hasn't picked a sort.
const DEFAULT_SORT: CompletedReviewsSort = 'finishDate,desc'

type RankStyle = { bg: string; text: string; label: string }

const RANK_STYLES: Record<TalentRankKey, RankStyle> = {
  TEN_X: { bg: '#15803D', text: '#FFFFFF', label: '10X' },
  NINE_X: { bg: '#22C55E', text: '#FFFFFF', label: '9X' },
  CORE: { bg: '#FEF9C3', text: '#854D0E', label: 'Core' },
  MISMATCH: { bg: '#FEE2E2', text: '#B91C1C', label: 'Mismatch' },
}

const TalentRankBadge = ({ rank }: { rank: TalentRankKey | null }): JSX.Element => {
  if (!rank) return <span className="text-stone-gray">—</span>
  const { bg, text, label } = RANK_STYLES[rank]
  return (
    <span className="rounded-full px-2 py-0.5 text-lg font-medium" style={{ backgroundColor: bg, color: text }}>
      {label}
    </span>
  )
}

const CompletedReviewsTable = ({
  rows,
  loading,
  error,
  page,
  pageSize,
  totalElements,
  sort,
  onPageChange,
  onPageSizeChange,
  onSortChange,
  onRetry,
}: Props): JSX.Element => {
  const headerProps = useReviewsTableSort<CompletedReviewsSortField>(sort, DEFAULT_SORT, onSortChange)

  const headerCells = (
    <>
      <SortableHeader col="employeeName" label="Employee" {...headerProps} />
      <SortableHeader col="reviewerName" label="Reviewer" {...headerProps} />
      <SortableHeader col="periodName" label="Period" {...headerProps} />
      <SortableHeader col="finishDate" label="Finish Date" {...headerProps} />
      <SortableHeader col="talentRank" label="Talent Rank" {...headerProps} />
      <SortableHeader col="averageScore" label="Avg. Score" {...headerProps} />
      <th scope="col" className="py-3 text-left text-xl font-semibold text-stone-gray">
        Action
      </th>
    </>
  )

  const bodyRows = rows.map(row => (
    <tr key={row.reviewId} className="odd:bg-gray-cell even:bg-gray-cell/50 hover:bg-just-gray">
      <EmployeeCell name={row.employeeName} email={row.employeeEmail} />
      <td className="py-4 pr-4 text-blue-gray">{row.reviewerName}</td>
      <td className="py-4 pr-4 text-blue-gray">{row.periodName}</td>
      <td className="py-4 pr-4 text-blue-gray">{formatDateLabel(row.finishDate)}</td>
      <td className="py-4 pr-4">
        <TalentRankBadge rank={row.talentRank} />
      </td>
      <td className="py-4 pr-4 text-blue-gray">
        {row.averageScore === null ? <span className="text-stone-gray">—</span> : row.averageScore.toFixed(1)}
      </td>
      <td className="py-4">
        <Link
          to={createPathToEmployeeResults(row.employeeId)}
          className="inline-flex items-center gap-2 rounded-md text-orange hover:underline"
        >
          <DocumentNavigationSVG />
          <span className="hidden text-xl font-semibold lg:inline">See Report</span>
        </Link>
      </td>
    </tr>
  ))

  return (
    <ReviewsTableShell
      ariaLabel="Completed reviews"
      caption="List of completed reviews with employee, period, and talent rank details"
      navAriaLabel="Completed reviews pagination"
      emptyText="No completed reviews found"
      isEmpty={rows.length === 0}
      loading={loading}
      error={error}
      onRetry={onRetry}
      page={page}
      pageSize={pageSize}
      totalElements={totalElements}
      onPageChange={onPageChange}
      onPageSizeChange={onPageSizeChange}
      headerCells={headerCells}
      bodyRows={bodyRows}
    />
  )
}

export default CompletedReviewsTable
